import { useEffect, useState } from "react";
import { useOnChange, vibrate } from "../hooks.ts";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import { Kostka } from "./Kostka.tsx";
import { Kubek } from "./Kubek.tsx";

/**
 * Perudo — ekran rundy.
 *
 * Przyklejone są dwie rzeczy: **aktualny zakład u góry i własne kości z panelem
 * ruchu u dołu**. To jedyne dwie informacje potrzebne w każdej sekundzie rundy;
 * historia i stan innych graczy mogą się przewijać. Kości u dołu, bo tam sięga
 * kciuk, a zakład u góry, bo się go czyta, a nie dotyka.
 *
 * Ekran nie zna żadnej reguły: legalne zakłady liczy serwer i przysyła jako
 * `minIleDla` — sześć liczb i sufit. Podpowiedzi prawdopodobieństwa tu nie ma
 * i nie będzie: policzenie tego **jest** grą.
 */

export interface Zaklad {
  gracz: string;
  ile: number;
  oczko: number;
}

export interface PerudoView {
  faza: "lobby" | "licytacja" | "odkrycie" | "koniec";
  roundNo: number;
  hostId: string;
  players: string[];
  canStart: boolean;

  aktywnyId: string | null;
  mojaTura: boolean;
  zaczynaId: string | null;

  mojeKosci: number[];
  /** `null` na każdej cudzej pozycji w trakcie licytacji. */
  kubki: Array<number[] | null>;
  iloscKosci: number[];
  kosciNaStole: number;
  kosciNaStart: number;

  zaklad: Zaklad | null;
  historia: Zaklad[];
  rozstrzygniecie: {
    typ: "dudo" | "calza";
    wolajacy: string;
    zaklad: Zaklad;
    policzono: number;
    trafil: boolean;
    traci: string | null;
    zyskuje: string | null;
    kubki: number[][];
    palifico: boolean;
  } | null;

  palifico: boolean;
  jedynkiDzikie: boolean;
  palificoZuzyte: string[];
  odpadli: string[];

  legalneZaklady: { minIleDla: Array<number | null>; maxIle: number } | null;
  mogeDudo: boolean;
  mogeCalza: boolean;
  mogeDalej: boolean;
}

const OCZKA = [1, 2, 3, 4, 5, 6];

/** Barwa oczek własnej kości: dzika jedynka jest jedynym sygnałem, jaki gra daje za darmo. */
function kolorOczka(wartosc: number, dzikie: boolean): string {
  return dzikie && wartosc === 1 ? "var(--perudo-dzika)" : "var(--perudo-oczko)";
}

/**
 * Licznik dobijający od zera do wyniku.
 *
 * Remount po `roundNo` (klucz na elemencie), a nie porównanie wartości: dwa
 * razy z rzędu „cztery" wyglądałoby jak zawieszona gra. To jedyna chwila,
 * w której gra pokazuje prawdę — musi ją pokazać widocznie.
 */
function useLicznik(cel: number): number {
  const [wartosc, setWartosc] = useState(0);

  useEffect(() => {
    if (cel <= 0) return;
    let biezaca = 0;
    const timer = setInterval(() => {
      biezaca += 1;
      setWartosc(biezaca);
      if (biezaca >= cel) clearInterval(timer);
    }, 90);
    return () => clearInterval(timer);
  }, [cel]);

  return wartosc;
}

export function Perudo({
  view,
  nicki,
  mojeId,
  onStart,
  onZaklad,
  onDudo,
  onCalza,
  onDalej,
}: {
  view: PerudoView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onZaklad: (ile: number, oczko: number) => void;
  onDudo: () => void;
  onCalza: () => void;
  onDalej: () => void;
}) {
  const [oczko, setOczko] = useState<number | null>(null);
  const [ile, setIle] = useState(1);

  // Nowy zakład na stole unieważnia wybór — minimalne ilości właśnie się zmieniły.
  const znacznik = `${view.roundNo}:${view.zaklad?.gracz ?? ""}:${view.zaklad?.ile ?? 0}:${view.zaklad?.oczko ?? 0}`;
  useEffect(() => {
    setOczko(null);
    setIle(1);
  }, [znacznik]);

  useOnChange(view.mojaTura, (teraz) => {
    if (teraz) vibrate([40, 60, 40]);
  });

  if (view.faza === "lobby") {
    return <Lobby view={view} nicki={nicki} onStart={onStart} />;
  }

  const nick = (id: string | null) => (id ? (nicki[id] ?? id) : "?");

  return (
    <>
      {/*
        Aktualny zakład u góry: pod nim przewija się stół i historia, a to jest
        liczba, o którą idzie spór. Tło kryjące, ujemne marginesy na całą szerokość.
      */}
      <div className="sticky top-0 z-10 -mx-4 border-b border-obrys bg-tlo px-4 pb-3 pt-2">
        <BiezacyZaklad view={view} nick={nick} />
      </div>

      <Stol view={view} nicki={nicki} mojeId={mojeId} />

      {view.faza === "odkrycie" && view.rozstrzygniecie ? (
        <Odkrycie key={view.roundNo} view={view} nick={nick} />
      ) : null}

      {view.faza === "koniec" ? <Wyniki view={view} nicki={nicki} /> : null}

      <Historia view={view} nick={nick} />

      {view.faza === "koniec" ? null : (
        <div className="sticky bottom-0 -mx-4 border-t border-obrys bg-tlo px-4 pb-2 pt-3 space-y-3">
          <MojeKosci view={view} />

          {view.faza === "odkrycie" ? (
            view.mogeDalej ? (
              <Przycisk wariant="glowny" pelna onClick={onDalej}>
                Dalej
              </Przycisk>
            ) : (
              <div className="text-center text-tekst-slaby">
                Nową rundę otwiera {nick(view.zaczynaId)}
              </div>
            )
          ) : view.mojaTura ? (
            <PanelRuchu
              view={view}
              oczko={oczko}
              ile={ile}
              onOczko={(wybrane, minimum) => {
                setOczko(wybrane);
                setIle(minimum);
              }}
              onIle={setIle}
              onZaklad={onZaklad}
              onDudo={onDudo}
              onCalza={onCalza}
            />
          ) : (
            <div className="text-center text-tekst-slaby">Ruch: {nick(view.aktywnyId)}</div>
          )}
        </div>
      )}
    </>
  );
}

function Lobby({
  view,
  nicki,
  onStart,
}: {
  view: PerudoView;
  nicki: Record<string, string>;
  onStart: () => void;
}) {
  return (
    <>
      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
        <div className="space-y-1">
          {view.players.map((id) => (
            <div key={id} className="flex justify-between">
              <span>{nicki[id] ?? id}</span>
              {id === view.hostId ? <Odznaka>host</Odznaka> : null}
            </div>
          ))}
        </div>
      </Karta>
      <Komunikat>
        Każdy ma {view.kosciNaStart} kości pod kubkiem i widzi tylko swoje. Licytujecie, ile kości
        o danym oczku leży na całym stole — jedynki liczą się jako każde oczko. Kto nie wierzy,
        woła „kłamiesz”; kto trafi co do jednej, woła „dokładnie tyle” i odzyskuje kość. Kto się
        pomyli, traci kość. Zostaje jeden.
      </Komunikat>
      {view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Zaczynamy
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie partię.</Komunikat>
      )}
    </>
  );
}

function BiezacyZaklad({ view, nick }: { view: PerudoView; nick: (id: string | null) => string }) {
  const zaklad = view.zaklad;

  return (
    <div className="text-center space-y-1">
      {zaklad ? (
        <>
          <div className="flex items-center justify-center gap-2">
            <span className="text-3xl font-semibold tabular">{zaklad.ile}</span>
            <span className="text-tekst-slaby text-2xl">×</span>
            <span className="w-12">
              <Kostka
                wartosc={zaklad.oczko}
                rozmiar="mala"
                kolor={kolorOczka(zaklad.oczko, view.jedynkiDzikie)}
              />
            </span>
          </div>
          <div className="text-sm text-tekst-slaby">{nick(zaklad.gracz)}</div>
        </>
      ) : (
        <div className="text-lg text-tekst-slaby">
          {view.faza === "koniec" ? "Koniec partii" : `Otwiera ${nick(view.zaczynaId)}`}
        </div>
      )}

      <div className="flex items-center justify-center gap-2 text-sm text-tekst-slaby">
        <span>Runda {view.roundNo}</span>
        <span>·</span>
        <span className="tabular">{view.kosciNaStole} kości na stole</span>
        {view.palifico ? <Odznaka aktywna>PALIFICO</Odznaka> : null}
      </div>
      {view.palifico ? (
        <div className="text-sm text-uwaga">Jedynki nie są dzikie, oczko jest ustalone.</div>
      ) : null}
    </div>
  );
}

/** Rząd graczy: kubek albo odkryte kości, pod spodem nick i liczba kości. */
function Stol({
  view,
  nicki,
  mojeId,
}: {
  view: PerudoView;
  nicki: Record<string, string>;
  mojeId: string;
}) {
  const zaliczona = (wartosc: number): boolean => {
    const r = view.rozstrzygniecie;
    if (!r) return false;
    if (wartosc === r.zaklad.oczko) return true;
    return !r.palifico && view.jedynkiDzikie && wartosc === 1 && r.zaklad.oczko !== 1;
  };

  return (
    <div className="flex flex-wrap justify-center gap-3">
      {view.players.map((id, i) => {
        const kubek = view.kubki[i] ?? null;
        const odpadl = view.iloscKosci[i] === 0;
        const aktywny = view.aktywnyId === id;

        return (
          <div
            key={id}
            className={[
              "flex flex-col items-center gap-1 rounded-2xl border px-2 py-2 min-w-20",
              aktywny ? "border-akcent" : "border-obrys",
              odpadl ? "opacity-40" : "",
            ].join(" ")}
          >
            <div className="flex h-8 items-end justify-center gap-0.5">
              {odpadl ? (
                <span className="text-sm text-tekst-slaby">odpadł</span>
              ) : kubek ? (
                kubek.map((wartosc, k) => (
                  <span
                    key={k}
                    className="kubek-odkrycie"
                    style={{ ["--kubek-opoznienie" as string]: `${i * 80 + k * 30}ms` }}
                  >
                    <Kostka
                      wartosc={wartosc}
                      rozmiar="mikro"
                      kolor={
                        zaliczona(wartosc) ? "var(--perudo-trafiona)" : "var(--perudo-oczko)"
                      }
                    />
                  </span>
                ))
              ) : (
                <Kubek rozmiar="mikro" />
              )}
            </div>
            <div className="text-xs truncate max-w-20">
              {id === mojeId ? "Ty" : (nicki[id] ?? id)}
            </div>
            <div className="text-xs tabular text-tekst-slaby">{view.iloscKosci[i]}</div>
          </div>
        );
      })}
    </div>
  );
}

/** Wynik rundy: co było pod kubkami i kto za to zapłacił. */
function Odkrycie({ view, nick }: { view: PerudoView; nick: (id: string | null) => string }) {
  const r = view.rozstrzygniecie;
  const policzono = useLicznik(r?.policzono ?? 0);
  if (!r) return null;

  const wolanie = r.typ === "dudo" ? "Kłamiesz!" : "Dokładnie tyle!";

  return (
    <Karta>
      <div className="text-center space-y-2">
        <div className="text-sm text-tekst-slaby">
          {nick(r.wolajacy)}: {wolanie}
        </div>
        <div className="flex items-center justify-center gap-2">
          <span className="text-4xl font-semibold tabular">{policzono}</span>
          <span className="text-tekst-slaby">z zakładu</span>
          <span className="tabular">
            {r.zaklad.ile} × {r.zaklad.oczko}
          </span>
        </div>
        {r.palifico ? (
          <div className="text-sm text-uwaga">Palifico — jedynki się nie liczyły.</div>
        ) : null}
        {r.traci ? (
          <div className="text-blad">{nick(r.traci)} traci kość</div>
        ) : r.zyskuje ? (
          <div className="text-akcent">{nick(r.zyskuje)} odzyskuje kość</div>
        ) : (
          <div className="text-tekst-slaby">Nikt nic nie traci</div>
        )}
      </div>
    </Karta>
  );
}

/** Historia bieżącej rundy. Znika razem z rundą — przy stole też nikt jej nie spisuje. */
function Historia({ view, nick }: { view: PerudoView; nick: (id: string | null) => string }) {
  if (view.historia.length === 0) return null;

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-2">Zakłady tej rundy</div>
      <div className="space-y-1">
        {view.historia.map((zaklad, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="flex-1 truncate">{nick(zaklad.gracz)}</span>
            <span className="tabular">
              {zaklad.ile} × {zaklad.oczko}
            </span>
          </div>
        ))}
      </div>
    </Karta>
  );
}

function MojeKosci({ view }: { view: PerudoView }) {
  if (view.mojeKosci.length === 0) {
    return <div className="text-center text-tekst-slaby">Nie masz już kości — oglądasz.</div>;
  }

  return (
    <div className="flex justify-center gap-1">
      {view.mojeKosci.map((wartosc, i) => (
        <span
          key={i}
          className="kubek-odkrycie w-12"
          /* Klucz po numerze rundy remontuje rząd, więc animacja idzie przy każdym rzucie. */
          style={{ ["--kubek-opoznienie" as string]: `${i * 60}ms` }}
        >
          <Kostka
            wartosc={wartosc}
            rozmiar="mala"
            kolor={kolorOczka(wartosc, view.jedynkiDzikie)}
          />
        </span>
      ))}
    </div>
  );
}

/**
 * Panel ruchu: sześć oczek w siatce 3×2, ilość i trzy decyzje.
 *
 * Siatka, nie rząd sześciu: rząd na pięciu calach daje pola po 48 px i trafia
 * się w sąsiada. Oczko niemożliwe (zmiana oczka w palifico, zakład ponad sufit)
 * jest wyłączone — reguły przyszły z serwera, ekran ich nie liczy.
 */
function PanelRuchu({
  view,
  oczko,
  ile,
  onOczko,
  onIle,
  onZaklad,
  onDudo,
  onCalza,
}: {
  view: PerudoView;
  oczko: number | null;
  ile: number;
  onOczko: (oczko: number, minimum: number) => void;
  onIle: (ile: number) => void;
  onZaklad: (ile: number, oczko: number) => void;
  onDudo: () => void;
  onCalza: () => void;
}) {
  const legalne = view.legalneZaklady;
  const maks = legalne?.maxIle ?? 0;
  const minDla = (o: number): number | null => {
    const min = legalne?.minIleDla[o - 1] ?? null;
    return min !== null && min <= maks ? min : null;
  };
  const mozliwe = OCZKA.some((o) => minDla(o) !== null);
  const minimum = oczko !== null ? minDla(oczko) : null;

  return (
    <div className="space-y-2">
      {mozliwe ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            {OCZKA.map((o) => {
              const min = minDla(o);
              return (
                <button
                  key={o}
                  disabled={min === null}
                  onClick={() => onOczko(o, min ?? 1)}
                  className={[
                    "flex min-h-14 items-center justify-center gap-2 rounded-2xl border",
                    oczko === o ? "border-akcent" : "border-obrys",
                    min === null ? "opacity-30" : "active:scale-95 transition-transform",
                  ].join(" ")}
                >
                  <span className="w-9">
                    <Kostka
                      wartosc={o}
                      rozmiar="mala"
                      kolor={kolorOczka(o, view.jedynkiDzikie)}
                    />
                  </span>
                  <span className="text-xs tabular text-tekst-slaby">
                    {min === null ? "—" : `od ${min}`}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <Przycisk
              onClick={() => onIle(Math.max(minimum ?? 1, ile - 1))}
              disabled={minimum === null || ile <= minimum}
              aria-label="Mniej kości"
            >
              −
            </Przycisk>
            <span className="flex-1 text-center tabular text-xl">
              {oczko === null ? "wybierz oczko" : `${ile} × ${oczko}`}
            </span>
            <Przycisk
              onClick={() => onIle(Math.min(maks, ile + 1))}
              disabled={minimum === null || ile >= maks}
              aria-label="Więcej kości"
            >
              +
            </Przycisk>
          </div>

          <Przycisk
            wariant="glowny"
            pelna
            disabled={minimum === null || ile < minimum || ile > maks}
            onClick={() => oczko !== null && onZaklad(ile, oczko)}
          >
            Podbijam
          </Przycisk>
        </>
      ) : (
        <Komunikat>
          Wyżej się nie da — zostaje „kłamiesz” albo „dokładnie tyle”.
        </Komunikat>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Przycisk wariant="blad" disabled={!view.mogeDudo} onClick={onDudo}>
          Kłamiesz!
        </Przycisk>
        <Przycisk wariant="gorny" disabled={!view.mogeCalza} onClick={onCalza}>
          Dokładnie tyle
        </Przycisk>
      </div>
    </div>
  );
}

/**
 * Wyniki: kolejność odpadania odwrócona.
 *
 * To jedyna miara, jaką ta gra ma z siebie — nie ma punktów, nie ma majątku,
 * jest tylko to, kto wypadł wcześniej.
 */
function Wyniki({ view, nicki }: { view: PerudoView; nicki: Record<string, string> }) {
  const kolejnosc = [
    ...view.players.filter((id) => !view.odpadli.includes(id)),
    ...[...view.odpadli].reverse(),
  ];

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-3">Wynik końcowy</div>
      <div className="space-y-2">
        {kolejnosc.map((id, miejsce) => (
          <div key={id} className="flex items-center gap-3">
            <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
            <span className="flex-1 truncate">
              {nicki[id] ?? id}
              {miejsce === 0 ? " 🏆" : ""}
            </span>
          </div>
        ))}
      </div>
    </Karta>
  );
}
